import { useRef, useState, useEffect } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useInView,
  useMotionValueEvent,
} from "framer-motion";

function TimelineItem({ item, index, scrollYProgress, containerRef }) {
  const itemRef = useRef(null);
  const dotRef = useRef(null);
  const isInView = useInView(itemRef, { once: true, amount: 0.3 });
  const [dotActive, setDotActive] = useState(false);
  const thresholdRef = useRef(1); // default: wait until line reaches it

  useEffect(() => {
    if (!dotRef.current || !containerRef.current) return;

    // Compute dot center's Y position as a fraction of container height,
    // using absolute page positions so scroll offset doesn't affect the result.
    const containerTop =
      containerRef.current.getBoundingClientRect().top + window.scrollY;
    const dotRect = dotRef.current.getBoundingClientRect();
    const dotCenterY =
      dotRect.top + window.scrollY + dotRect.height / 2 - containerTop;

    thresholdRef.current = dotCenterY / containerRef.current.offsetHeight;

    // Sync immediately in case the page loaded mid-scroll
    setDotActive(scrollYProgress.get() >= thresholdRef.current);
  }, [containerRef, scrollYProgress]);

  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    setDotActive(latest >= thresholdRef.current);
  });

  return (
    <div ref={itemRef} className="tl-item">
      <div ref={dotRef} className="tl-dot">
        <motion.div
          className="tl-dot__fill"
          initial={{ scale: 0 }}
          animate={{ scale: dotActive ? 1 : 0 }}
          transition={{ duration: 0.3, ease: "backOut" }}
        />
      </div>
      <motion.div
        className="tl-content"
        initial={{ opacity: 0, y: 14 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.45, delay: 0.2, ease: "easeOut" }}
      >
        <span className="tl-num">{String(index + 1).padStart(2, "0")}</span>
        <h4 className="tl-title">{item.title}</h4>
        <p className="tl-desc">{item.description}</p>
      </motion.div>
    </div>
  );
}

function Timeline({ items }) {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 85%", "end 30%"],
  });
  const scaleY = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <div ref={containerRef} className="tl">
      <motion.div
        className="tl__line-fill"
        style={{ scaleY, transformOrigin: "top" }}
      />
      {items.map((item, index) => (
        <TimelineItem
          key={item.id}
          item={item}
          index={index}
          scrollYProgress={scrollYProgress}
          containerRef={containerRef}
        />
      ))}
    </div>
  );
}

export default Timeline;
